import React, { useEffect, useRef } from 'react'

import { useStableHandler } from '@sketch/utils'
import type { EntityOverlayMode, FramesDict } from '@sketch/canvas'

const OPEN_DROPDOWN_TRIGGER = '[aria-haspopup][aria-expanded="true"]'

type KeyboardShortcutsOptions = {
  mode: EntityOverlayMode
  selectedEntityUUID: string | null
  clearSelection: () => void
  framesRefs: React.RefObject<FramesDict>
  /** The canvas itself: only a click inside it is a click off the entity. */
  canvasRef: React.RefObject<HTMLElement | null>
  /**
   * Whether the renderer's camera is moving, i.e. the canvas is being panned or
   * zoomed. Taken as a value rather than read from the renderer here so this
   * hook stays usable - and testable - outside a renderer provider.
   */
  isCameraMoving: boolean
}

/**
 * Escape deselects the selected entity, unless a dropdown is open - then it
 * only closes that. Clicking off the entity on the canvas deselects it too.
 *
 * Enter is deliberately *not* here. Opening the selected entity is the job of
 * its own CTA, which takes focus when it is selected, so Enter reaches it the
 * way it reaches any focused button. A global listener could only guess from
 * `document.activeElement` whether Enter was meant for the canvas, and it
 * guessed wrong: a toolbar button that merely kept focus from an earlier click
 * is indistinguishable from one the user is about to press, so Enter silently
 * stopped opening frames.
 *
 * Both are off only while inspecting, where a click on empty canvas already
 * clears the selection through the scene hit-test - which knows what was
 * actually under the pointer rather than inferring it from the overlay bounds.
 *
 * A pan is not a click. The pointer goes down on empty canvas, the camera moves
 * under it, and the browser still reports a click on release - which this used
 * to read as "clicked off the entity" and deselect. That took the selection
 * away every time someone panned with something selected, which this branch
 * made far easier to hit by keeping selections across closing a panel and
 * switching between View and Inspect.
 *
 * The renderer already says when its camera is moving, so that is what decides
 * it rather than a guess from how far the pointer travelled: the flag is latched
 * for the duration of a press, because by the time the click arrives the pan has
 * ended and the camera is at rest again.
 *
 * Note this is deliberately *not* gated on the overlays being interactive.
 * Placing a draft annotation makes them `disabled`, but the click that drops
 * the pin still deselects, exactly as it did before entity overlays had modes.
 * Deselecting is a canvas-level gesture rather than an interaction with an
 * entity, and Escape is not gated either - gating one and not the other is what
 * made them disagree.
 */
export function useEntityKeyboardShortcuts({
  mode,
  selectedEntityUUID,
  clearSelection,
  framesRefs,
  canvasRef,
  isCameraMoving,
}: KeyboardShortcutsOptions) {
  const cameraMovedDuringPress = useRef(false)

  // Latched rather than read at click time: the pan is over by then.
  useEffect(() => {
    if (isCameraMoving) cameraMovedDuringPress.current = true
  }, [isCameraMoving])

  const handleWindowKeyPress = useStableHandler((event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !selectedEntityUUID) return

    // The key is the dropdown's to close it with. Its trigger still reads as
    // open here because the dropdown has yet to handle the key: `Dropdown`
    // listens on window, which the key reaches after the body, and
    // `useResponsiveDropdown` listens on the body too, so this relies on being
    // added first. Re-adding it while one is open would let that go first.
    if (document.querySelector(OPEN_DROPDOWN_TRIGGER)) return

    clearSelection()
  })

  // Each press starts over, so the latch only ever reports movement that
  // happened while this pointer was down. Without this, a camera move with no
  // press behind it - a wheel zoom, or zoom-to-fit - would leave the flag set
  // and swallow the next genuine click.
  const handleWindowPointerDown = useStableHandler(() => {
    cameraMovedDuringPress.current = false
  })

  // Handle click outside to deselect the currently selected entity
  const handleWindowClick = useStableHandler(
    (event: MouseEvent | TouchEvent) => {
      // The click that ends a pan is the pan, not a click on the canvas.
      if (cameraMovedDuringPress.current) {
        cameraMovedDuringPress.current = false
        return
      }

      // Only a click on the canvas is a click off the entity. The sidebar, the
      // header and the menus they open are somewhere else entirely.
      if (!canvasRef.current?.contains(event.target as Node)) return

      // Checking if the click on the overlay was inside one of the artboards,
      // If not, we deselect the selected artboard
      for (const frameId in framesRefs.current) {
        if (framesRefs.current[frameId]?.contains(event.target as Node)) {
          return
        }
      }

      if (selectedEntityUUID) clearSelection()
    }
  )

  useEffect(() => {
    if (!selectedEntityUUID) {
      return
    }

    window.document.body.addEventListener('keydown', handleWindowKeyPress)

    if (mode === 'inspect') {
      return () => {
        window.document.body.removeEventListener(
          'keydown',
          handleWindowKeyPress
        )
      }
    }

    // Attached on the next task, not this one. A selection can come *into*
    // existence during a click - dismissing the inspector collapses the
    // selection to the nearest entity, and the click that dismissed it landed
    // on the toolbar - and that click is still propagating when this effect
    // runs. Attaching synchronously means it reaches this listener, which sees
    // a target outside every overlay and deselects what was just selected.
    const attach = window.setTimeout(() => {
      window.document.body.addEventListener('click', handleWindowClick)
    })

    // Not deferred: this only resets a flag, and it has to be listening before
    // the press that a pan starts with.
    window.document.body.addEventListener('mousedown', handleWindowPointerDown)

    return () => {
      window.clearTimeout(attach)
      window.document.body.removeEventListener('keydown', handleWindowKeyPress)
      window.document.body.removeEventListener('click', handleWindowClick)
      window.document.body.removeEventListener(
        'mousedown',
        handleWindowPointerDown
      )
    }
  }, [
    handleWindowClick,
    handleWindowKeyPress,
    handleWindowPointerDown,
    selectedEntityUUID,
    mode,
  ])
}
